import { connect } from 'react-redux';
import { goBack, push } from 'redux-first-history';
import { formatDate, formatYMDStringToTimestamp } from '@common/paymentsUtils/dateUtils';
import queryString from 'query-string';
import EditTermLengthModal from './component';
import {
  DEFAULT_ERROR,
  PAYMENT_SETTINGS_UPDATED,
  STOP_PAYMENTS_SUCCESS,
} from '@rental-manager/constants/NotificationConstants';
import { getIreneDashboard, stopPayments } from '@rental-manager/actions/requests';
import { closeNotification, showModalNotification, showPageNotification } from '@common/actions/NotificationActions';
import { trackValidationError } from '@common/actions/TrackingActions';
import { getSignInPage } from '@common/paymentsUtils/api';
import { getPaymentErrorInfo } from '../../../../utils/getPaymentErrorInfo';
import type { IRentTermsModel } from '@rental-manager/models/Types';
import paths from '@rental-manager/pathsManifest';
import HTTP_STATUS from '@common/constants/HttpConstants';
import { editingRentTerms } from '@rental-manager/actions/DashboardActions';
import { logError } from '@zg-rentals/log-error';
import type { UpdateRentTermsWebApiRequestDto } from '@zg-rentals/ts-rent-payment-client';
import { UpdateRentTermsWebApiHandler_updateRentTerms } from '@zg-rentals/ts-rent-payment-client';

const mapStateToProps = (state, ownProps) => {
  const rentTermsKeyFromQueryString = queryString.parse(ownProps.location.search).rentTermsKey;
  const { currentTerm, nextTerm, listingAlias, selectedLease } = state.payments.landlord.property;

  let rentTerm;
  if (rentTermsKeyFromQueryString && nextTerm?.rentTermsKey === rentTermsKeyFromQueryString) {
    rentTerm = nextTerm;
  } else if (rentTermsKeyFromQueryString && currentTerm?.rentTermsKey === rentTermsKeyFromQueryString) {
    rentTerm = currentTerm;
  }

  const {
    numberOfPayments,
    firstDueDate,
    monthToMonth,
    rentTermsKey,
    rentalProtectionPolicy,
    paymentDateForFirstMonth,
    dueLastDayOfMonth: persistedDueLastDayOfMonth,
  } = rentTerm;

  const { paymentDayOfTheMonth } = state.payments.landlord.property.editingRentTerms || {};
  const { paymentsEncodedLeaseId } = selectedLease || {};

  return {
    today: new Date(),
    rentTermsKey,
    showOneTimePayment: true,
    firstDueDate,
    paymentDateForFirstMonth,
    numberOfPayments: numberOfPayments && Number(numberOfPayments),
    monthToMonth,
    monthlyRent: rentTerm.amount,
    paymentDayOfTheMonth: !paymentDayOfTheMonth
      ? Number(formatDate(paymentDateForFirstMonth, 'd'))
      : paymentDayOfTheMonth,
    dueLastDayOfMonth: paymentDayOfTheMonth != null ? paymentDayOfTheMonth === 31 : persistedDueLastDayOfMonth,
    currentTermRentTermsKey: currentTerm?.rentTermsKey ? currentTerm.rentTermsKey : '',
    rentalProtectionPolicyStatus: rentalProtectionPolicy && rentalProtectionPolicy.policyStatus,
    listingAlias,
    paymentsEncodedLeaseId,
    user: state.user,
    currentProperty: state.payments.landlord.property.listingDetails,
  };
};

export const mapDispatchToProps = (dispatch, { baseUrl, propertyId }) => ({
  requestStopPayments: async (rentTermsKey) => {
    try {
      await dispatch(closeNotification());
      await dispatch(stopPayments({ propertyId, rentTermsKey }));
      await dispatch(push(baseUrl + paths.dashboard.stopPaymentsFeedbackPage));
      await dispatch(showModalNotification(STOP_PAYMENTS_SUCCESS));
      return { error: false };
    } catch (err) {
      const { httpStatus } = getPaymentErrorInfo(err);
      if (httpStatus === HTTP_STATUS.SC_UNAUTHORIZED) {
        window.location = getSignInPage();
        return null;
      }
      logError({ error: err });
      dispatch(showModalNotification(DEFAULT_ERROR));
      return { error: true };
    }
  },
  onSubmit: (newRentTerm: IRentTermsModel, paymentsEncodedLeaseId, dueLastDayOfMonth: boolean) =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => {
        const paymentLength = newRentTerm.monthToMonth
          ? { monthToMonth: true }
          : {
              numberOfPayments: newRentTerm.numberOfPayments,
            };

        const updateRentTermsPayload: UpdateRentTermsWebApiRequestDto = {
          resourceId: propertyId,
          rentTermsKey: newRentTerm.rentTermsKey,
          amount: Number(newRentTerm.monthlyRent),
          firstDueDate: formatYMDStringToTimestamp(newRentTerm.firstDueDate),
          paymentsEncodedLeaseId,
          dueLastDayOfMonth,
          ...paymentLength,
        };

        return UpdateRentTermsWebApiHandler_updateRentTerms(updateRentTermsPayload);
      })
      .then(() => dispatch(editingRentTerms({ propertyId, paymentDayOfTheMonth: null })))
      .then(() => dispatch(push(baseUrl)))
      .then(() => dispatch(getIreneDashboard({ propertyId })))
      .then(() => dispatch(showPageNotification(PAYMENT_SETTINGS_UPDATED)))
      .catch((err) => {
        logError({ error: err });
        dispatch(showModalNotification(DEFAULT_ERROR));
        return { error: true };
      }),
  onCancel: () =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => dispatch(editingRentTerms({ propertyId, paymentDayOfTheMonth: null })))
      .then(() => dispatch(push(baseUrl))),
  onBack: () =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => dispatch(goBack())),
  onClose: () =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => dispatch(editingRentTerms({ propertyId, paymentDayOfTheMonth: null })))
      .then(() => dispatch(push(baseUrl)))
      .then(() => dispatch(getIreneDashboard({ propertyId }))),
  trackValidationError: (data) => dispatch(trackValidationError(data)),
});

export const mergeProps = (stateProps, dispatchProps) => ({
  ...stateProps,
  ...dispatchProps,
  onSubmit: (newRentTerm) =>
    dispatchProps.onSubmit(
      { ...newRentTerm, monthlyRent: stateProps.monthlyRent },
      stateProps.paymentsEncodedLeaseId,
      stateProps.dueLastDayOfMonth,
    ),
});

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(EditTermLengthModal);
